import type {
  DraftState,
  PlannedOrderSettings,
  WizardFile,
  WizardReleasedFile,
  WizardSampleGroup
} from '../../Model/OrderWizard/OrderWizardDraft.ts'
import type { IOrder } from '../../Model/Order/IOrder.ts'
import type { DetectedSequencingPlatform } from '../../Util/sequencingPlatformDetection.ts'
import type { ServerSnapshot } from './reconcile.ts'
import { reconcileDraft } from './reconcile.ts'
import { primerKeyOf } from './sheet/applyPlan.ts'

const AS_ORDERED = 'as the order holds it'

/** The order's settings as the lab's own: planning from files dropped later keeps them. */
export const settingsOfOrder = (order: IOrder): PlannedOrderSettings => ({
  sampleConfiguration: { value: order.sampleConfiguration, source: 'entered', reason: AS_ORDERED },
  targetTaxonomy: { value: order.targetTaxonomy as PlannedOrderSettings['targetTaxonomy']['value'], source: 'entered', reason: AS_ORDERED },
  processingType: { value: order.processingType, source: 'entered', reason: AS_ORDERED },
  targetRegion: order.targetRegion,
  ...(order.targetRegion && order.targetRegion !== 'WORLDWIDE' ? { targetRegionEntered: order.targetRegion } : {}),
  allowSingletons: !!order.allowSingletons,
  allowSingletonsSource: 'entered',
  redlistMode: order.redlistMode ?? 'NONE'
})

const serverFileKey = (serverFileId: string) => `server:${serverFileId}`

/**
 * A draft for an unsubmitted order, built from what the server holds: its items become groups, their
 * files known by name only, so dropping them again attaches them. Uploaded files no item uses are kept
 * aside, so a drop takes them back without uploading again. The rest is the reconciliation every
 * restored draft goes through.
 */
export const draftFromOrder = (
  { order, snapshot, platforms }: { order: IOrder; snapshot: ServerSnapshot; platforms: Record<string, string> },
  draftId: string,
  now: string
): DraftState | undefined => {
  if (snapshot.order === 'missing' || snapshot.order.submitted) {
    return undefined
  }
  const uploaded = new Map(Object.values(snapshot.completed).flatMap((files) => files.map((file) => [file.id, file.fileName] as const)))
  const fileFor = (serverFileId: string): WizardFile => {
    const name = uploaded.get(serverFileId) as string
    return {
      name,
      size: 0,
      lastModified: 0,
      key: serverFileKey(serverFileId),
      kind: 'fastq',
      fromServer: true,
      ...(platforms[serverFileId] ? { platform: platforms[serverFileId] as DetectedSequencingPlatform } : {}),
      upload: { status: 'uploaded', progress: 1, serverFileId }
    }
  }
  const files: Record<string, WizardFile> = {}
  const groups: Record<string, WizardSampleGroup> = {}
  for (const item of snapshot.pools.flatMap((pool) => pool.items)) {
    const { forwardFileId, reverseFileId } = item
    /* an item whose files the order does not list as uploaded cannot be matched again; left to the classic view */
    if (!forwardFileId || !uploaded.has(forwardFileId) || (reverseFileId && !uploaded.has(reverseFileId))) {
      continue
    }
    const forward = fileFor(forwardFileId)
    const reverse = reverseFileId ? fileFor(reverseFileId) : undefined
    files[forward.key] = forward
    if (reverse) {
      files[reverse.key] = reverse
    }
    const itemType = item.itemType ?? 'SAMPLE'
    groups[forward.key] = {
      key: forward.key,
      forward: forward.key,
      ...(reverse ? { reverse: reverse.key } : {}),
      itemType,
      typeSource: 'user',
      item: { status: 'created', itemId: item.id, itemType },
      /* whatever the order's items carry was put there on purpose: nothing is written until a sheet or entry says otherwise */
      applied: { status: 'done', primers: primerKeyOf({}), itemType }
    }
  }
  const releasedFiles: WizardReleasedFile[] = [...uploaded]
    .filter(([id]) => !files[serverFileKey(id)])
    .map(([serverFileId, name]) => ({ name, serverFileId }))
  const pools = snapshot.pools.map((pool) => pool.id)
  const draft: DraftState = {
    id: draftId,
    name: order.name,
    ...(order.description ? { description: order.description } : {}),
    createdAt: order.orderCreated ?? now,
    phase: 'active',
    orderId: order.id,
    poolIds: pools,
    files,
    fileOrder: Object.keys(files),
    groups,
    settings: settingsOfOrder(order),
    questions: [],
    restored: true,
    ...(pools.length > 1 ? { poolsAnswered: true, poolsSource: 'entered' as const } : {}),
    ...(releasedFiles.length > 0 ? { releasedFiles } : {})
  }
  const outcome = reconcileDraft(draft, snapshot)
  return outcome.kind === 'keep' ? outcome.draft : undefined
}

/** With no draft open: the newest draft on arriving, a new one after the lab discarded the open draft. */
export const draftToOpen = (newestDraftId: string | undefined, startFresh: boolean): { activate: string } | 'create' =>
  newestDraftId && !startFresh ? { activate: newestDraftId } : 'create'
